import type { TranslateFunction } from '@locales/generated';
import { sortedBy } from '@shared/utils/sort';
import { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { useCardSizeLabels } from '@apps/card/utils/cardSizeName';
import { mapToLabel } from './components/Filters/facetOptions';
import type { Facet } from './components/Filters/Filter.types';
import { FACET_ORDER } from './components/Filters/Filters.constants';
import type { StationeryTemplate } from './DesignsGallery.types';

export const useFilterFacets = (templates: StationeryTemplate[]) => {
  const { t, tq } = useTranslation('stationery');
  const tFilterOptions = t('dashboard', 'designsGallery', 'filterOptions') as Record<string, TranslateFunction>;

  // The size facet's labels come from tq — unlike the `filterOptions` map above,
  // whose locale keys are externally maintained and can't take additions from this
  // change. These tq calls must sit lexically inside this hook (the translate
  // extractor cannot trace tq through function parameters). The postcard label is
  // the blessed product name from `useCardSizeLabels`; the classic 5x7 has no
  // blessed name, so it reads as a measurement.
  const { postcard4x6: sizePostcardLabel } = useCardSizeLabels();
  const sizeFacetLabel = tq`Size`;
  const sizeClassicLabel = tq`5 × 7`;

  return useMemo(() => {
    const localFacetLabels: Record<string, string> = {
      size: sizeFacetLabel,
      'size classic': sizeClassicLabel,
      'size postcard': sizePostcardLabel,
    };

    const facetOptionCounts = new Map<string, number>();
    for (const option of templates.flatMap(t => t.mappedTags)) {
      facetOptionCounts.set(option, (facetOptionCounts.get(option) ?? 0) + 1);
    }

    const facets = new Map<Facet, typeof facetOptionCounts>();
    for (const [facetOption, count] of Array.from(facetOptionCounts.entries())) {
      const facet = facetOption.split(':')[0] as Facet;
      facets.set(facet, (facets.get(facet) ?? new Map()).set(facetOption, count));
    }

    const facetsForUI = Array.from(facets.entries()).map(([facet, options]) => {
      return {
        groupCode: facet,
        label: tFilterOptions[facet]?.() ?? localFacetLabels[facet] ?? `[${facet}]`,
        options: Array.from(options.entries()).map(([option, count]) => {
          const translationKey = option.replace(':', ' '); // Don't use colon as separator, i18next will think it's a namespace
          return {
            compositeCode: option,
            label: tFilterOptions[translationKey]?.() ?? localFacetLabels[translationKey] ?? mapToLabel(option) ?? `[${translationKey}]`,
            matchingItemCount: count,
          };
        }),
      };
    });

    for (const facet of facetsForUI) {
      facet.options.sort((a, b) => a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }));
    }
    return sortedBy(facetsForUI, f => f.groupCode, FACET_ORDER);
  }, [tFilterOptions, templates, sizeFacetLabel, sizeClassicLabel, sizePostcardLabel]);
};
